import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useId,
} from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './WheelCarousel.css';

gsap.registerPlugin(ScrollTrigger);

function wrapIndex(index, length) {
  return ((index % length) + length) % length;
}

function shortestOffset(index, rotation, length) {
  let offset = index - rotation;
  while (offset > length / 2) offset -= length;
  while (offset < -length / 2) offset += length;
  return offset;
}

function formatWheelTitle(num, title) {
  const words = title.trim().split(/\s+/);
  if (words.length >= 3) {
    if (words[0].toLowerCase() === 'manage') {
      return {
        line1: 'Manage',
        line2: 'Your Risks',
      };
    }
    const mid = Math.ceil(words.length / 2);
    return {
      line1: words.slice(0, mid).join(' '),
      line2: words.slice(mid).join(' '),
    };
  } else if (words.length === 2) {
    return {
      line1: words[0],
      line2: words[1],
    };
  } else {
    return {
      line1: '',
      line2: title,
    };
  }
}

const fallbackImages = [
  '/media/modern-architecture-and-cityscape-in-an-urban-envi-2026-09-15-20-02-22-utc.webp',
  '/media/low-angle-view-modern-buildings-against-sky.webp',
  '/media/financial-growth-investment-success-with-stacks-gold-coins.webp',
  '/media/double-exposure-digital-graphs-with-stacked-coins.webp',
  '/media/sculpture-city-long-exposure-complex-amazing.webp',
];

export const WheelCarousel = ({
  items = [],
  radius = 280,
  spacing = 18,
  visibleItems = 5,
  apexInset = 22,
  showMarker = true,
  pinned = true,
  eyebrow = 'What governs our strategies',
  title = 'Investment Principles',
  lead = 'Here at ASTRID FIRST, our strategies are governed by five fundamental principles.',
  onActiveChange,
  className = '',
}) => {
  const instanceId = useId();
  const itemCount = items.length;

  const [rotation, setRotation] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const rotationRef = useRef(0);
  const selectedRef = useRef(0);
  const velocityRef = useRef(0);
  const draggingRef = useRef(false);
  const dragOriginRef = useRef({ y: 0, rotation: 0 });
  const previousDragRotationRef = useRef(0);
  const frameRef = useRef(null);

  const commitRotation = useCallback(
    (nextRotation) => {
      if (!itemCount) return;
      rotationRef.current = nextRotation;
      setRotation(nextRotation);
      const nextIndex = wrapIndex(Math.round(nextRotation), itemCount);
      if (nextIndex !== selectedRef.current) {
        selectedRef.current = nextIndex;
        setSelectedIndex(nextIndex);
        if (onActiveChange) {
          onActiveChange(items[nextIndex], nextIndex);
        }
      }
    },
    [itemCount, items, onActiveChange]
  );

  const runAnimation = useCallback(() => {
    if (frameRef.current !== null) return;

    const tick = () => {
      let keepAnimating = false;

      if (!draggingRef.current && Math.abs(velocityRef.current) > 0.0008) {
        commitRotation(rotationRef.current + velocityRef.current);
        velocityRef.current *= 0.92;
        keepAnimating = true;
      } else if (!draggingRef.current) {
        velocityRef.current = 0;
        const target = Math.round(rotationRef.current);
        const delta = target - rotationRef.current;
        if (Math.abs(delta) > 0.001) {
          commitRotation(rotationRef.current + delta * 0.22);
          keepAnimating = true;
        } else {
          commitRotation(target);
        }
      }

      if (keepAnimating) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        frameRef.current = null;
      }
    };

    frameRef.current = requestAnimationFrame(tick);
  }, [commitRotation]);

  useEffect(() => {
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  // GSAP ScrollTrigger pinning & scroll scrubbing (desktop only >= 64rem)
  useEffect(() => {
    if (!pinned || !containerRef.current || !stageRef.current || itemCount <= 1) return;

    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isReduced) return;

    const mm = gsap.matchMedia();
    mm.add('(min-width: 64rem)', () => {
      const st = ScrollTrigger.create({
        trigger: containerRef.current,
        start: 'top top',
        end: '+=1800',
        pin: stageRef.current,
        pinSpacing: true,
        scrub: 0.6,
        anticipatePin: 1,
        onUpdate: (self) => {
          const progress = self.progress;
          const targetRotation = progress * (itemCount - 1);
          commitRotation(targetRotation);
        },
      });

      return () => {
        st.kill();
      };
    });

    return () => {
      mm.revert();
    };
  }, [pinned, itemCount, commitRotation]);

  // Pointer drag listeners for optional direct interaction
  const handlePointerDown = (e) => {
    if (!e.isPrimary || e.button !== 0) return;
    draggingRef.current = true;
    setIsDragging(true);
    velocityRef.current = 0;
    dragOriginRef.current = { y: e.clientY, rotation: rotationRef.current };
    previousDragRotationRef.current = rotationRef.current;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    const distance = e.clientY - dragOriginRef.current.y;
    const nextRotation = dragOriginRef.current.rotation - distance * 0.015;
    velocityRef.current = nextRotation - previousDragRotationRef.current;
    previousDragRotationRef.current = nextRotation;
    commitRotation(nextRotation);
  };

  const handlePointerEnd = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setIsDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    runAnimation();
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      velocityRef.current = 0;
      commitRotation(Math.min(itemCount - 1, Math.round(rotationRef.current) + 1));
      runAnimation();
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      velocityRef.current = 0;
      commitRotation(Math.max(0, Math.round(rotationRef.current) - 1));
      runAnimation();
    }
  };

  // Click on a wheel item to rotate directly to it
  const handleItemClick = (index) => {
    velocityRef.current = 0;
    const offset = shortestOffset(index, rotationRef.current, itemCount);
    const target = rotationRef.current + offset;
    let current = rotationRef.current;

    const step = () => {
      const diff = target - current;
      if (Math.abs(diff) > 0.005) {
        current += diff * 0.25;
        commitRotation(current);
        requestAnimationFrame(step);
      } else {
        commitRotation(target);
      }
    };
    requestAnimationFrame(step);
  };

  if (!itemCount) return null;

  const safeIndex = wrapIndex(selectedIndex, itemCount);
  const activeItem = items[safeIndex] || items[0];

  return (
    <section
      ref={containerRef}
      className={`wheel-carousel-container ${pinned ? 'wheel-carousel-container--pinned' : ''} ${className}`}
    >
      <div
        ref={stageRef}
        role="region"
        aria-label="Investment Principles Wheel"
        tabIndex={0}
        className={`wheel-carousel-stage ${isDragging ? 'wheel-carousel-stage--dragging' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onKeyDown={handleKeyDown}
      >
        {/* ONE Master Container Box (100% width & height with screenshot grid texture) */}
        <div className="wheel-unified-box">
          {/* Main Grid: Photo (58%) : Wheel Arc (18%) : Explanation (24%) */}
          <div className="wheel-carousel-body">
            {/* Column 1 (58%): Photo fills the column, Title is IN the photo, Right side has white gradient overlay */}
            <div className="wheel-photo-col" aria-label={`Photo for ${activeItem.title}`}>
              {/* Photo Images with smooth crossfade */}
              {items.map((item, idx) => {
                const imgUrl = item.image || fallbackImages[idx % fallbackImages.length];
                return (
                  <img
                    key={`${item.num}-${idx}`}
                    src={imgUrl}
                    alt={item.title}
                    className={`wheel-photo-img ${idx === safeIndex ? 'wheel-photo-img--active' : ''}`}
                    loading="eager"
                    draggable={false}
                  />
                );
              })}

              {/* Protective Dark Gradient at the Bottom of the Photo for Title Legibility */}
              <div className="wheel-photo-gradient-bottom" aria-hidden="true" />

              {/* White Gradient Overlay on the Right Side of the Photo */}
              <div className="wheel-photo-gradient-right" aria-hidden="true" />

              {/* Content IN the Photo: Photo Number Guide + Section Title */}
              <div className="wheel-photo-content">
                {/* Photo Number Guide */}
                <div className="wheel-photo-guide">
                  <span className="wheel-guide-text">
                    {String(safeIndex + 1).padStart(2, '0')} / {String(itemCount).padStart(2, '0')}
                  </span>
                  <span className="wheel-guide-track">
                    <span
                      className="wheel-guide-fill"
                      style={{ width: `${((safeIndex + 1) / itemCount) * 100}%` }}
                    />
                  </span>
                </div>

                {/* Section Title IN the Photo */}
                <div className="wheel-photo-title-wrap">
                  {eyebrow && <p className="wheel-photo-eyebrow">{eyebrow}</p>}
                  {title && <h2 data-reveal="lines" className="wheel-photo-title">{title}</h2>}
                  {lead && <p data-reveal="fade" className="wheel-photo-lead">{lead}</p>}
                </div>
              </div>
            </div>

            {/* Column 2 (18%): Rotating Wheel Arc - 2 Lines Aligned Left (Desktop only >= 64rem) */}
            <div className="wheel-track-col">
              <div
                className="wheel-carousel-track-col"
                role="listbox"
                aria-label="Principles list"
                aria-activedescendant={`${instanceId}-item-${safeIndex}`}
              >
                {showMarker && (
                  <span
                    aria-hidden="true"
                    className="wheel-carousel-marker"
                    style={{ left: `calc(${apexInset}% - 16px)` }}
                  />
                )}

                {items.map((item, index) => {
                  const offset = shortestOffset(index, rotation, itemCount);
                  if (Math.abs(offset) > visibleItems + 1) return null;

                  const angle = offset * spacing;
                  const radians = (angle * Math.PI) / 180;
                  const x = -radius * (1 - Math.cos(radians));
                  const y = radius * Math.sin(radians);
                  const distance = Math.min(Math.abs(offset) / visibleItems, 1);
                  const opacity = Math.max(0, Math.cos((distance * Math.PI) / 2));
                  const scale = 1 - Math.min(Math.abs(offset) * 0.05, 0.35);
                  const isSelected = Math.abs(offset) < 0.5;
                  const { line1, line2 } = formatWheelTitle(item.num, item.title);

                  return (
                    <div
                      id={`${instanceId}-item-${index}`}
                      key={`${item.num}-${index}`}
                      role="option"
                      aria-selected={isSelected}
                      aria-label={`${item.num} ${item.title}`}
                      className={`wheel-carousel-item ${
                        isSelected ? 'wheel-carousel-item--selected' : ''
                      }`}
                      style={{
                        left: `${apexInset}%`,
                        opacity,
                        transform: `translate(${x}px, ${y}px) translateY(-50%) rotate(${angle}deg) scale(${scale})`,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleItemClick(index);
                      }}
                    >
                      <div className="wheel-item-content">
                        <span className="wheel-item-line wheel-item-line--1">
                          <span className="wheel-item-num">{item.num}</span>
                          {line1 && <span className="wheel-item-text-chunk">{line1}</span>}
                        </span>
                        <span className="wheel-item-line wheel-item-line--2">
                          {line2}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Column 3 (24%): Unboxed Active Principle Explanation */}
            <div className="wheel-right-col">
              <div className="wheel-active-explanation">
                <div className="wheel-explainer-badge">
                  <span className="wheel-explainer-num">{activeItem.num}</span>
                  <span className="wheel-explainer-kicker">Principle in Focus</span>
                </div>
                <h3 className="wheel-explainer-title">{activeItem.title}</h3>
                <p className="wheel-explainer-desc">{activeItem.description}</p>
              </div>

              {/* Mobile / Tablet Principle Navigation (active when wheel is hidden < 64rem) */}
              <div className="wheel-mobile-nav" aria-label="Principles navigation">
                <div className="wheel-mobile-dots">
                  {items.map((item, idx) => (
                    <button
                      key={`mobile-dot-${item.num}`}
                      type="button"
                      className={`wheel-mobile-dot ${idx === safeIndex ? 'is-active' : ''}`}
                      onClick={() => handleItemClick(idx)}
                      aria-label={`Principle ${item.num}: ${item.title}`}
                    >
                      {item.num}
                    </button>
                  ))}
                </div>
                <div className="wheel-mobile-arrows">
                  <button
                    type="button"
                    className="wheel-mobile-btn"
                    onClick={() => handleItemClick((safeIndex - 1 + itemCount) % itemCount)}
                    aria-label="Previous principle"
                  >
                    &larr; Prev
                  </button>
                  <button
                    type="button"
                    className="wheel-mobile-btn"
                    onClick={() => handleItemClick((safeIndex + 1) % itemCount)}
                    aria-label="Next principle"
                  >
                    Next &rarr;
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="wheel-carousel-scroll-hint" aria-hidden="true">
            <span>Scroll down or drag to navigate principles</span>
          </div>
        </div>
      </div>
    </section>
  );
};
